import type {
  BusinessOption,
  OnlineOrderFilters,
  PosOrderFilters,
  QueryParams,
} from "@/types/order-history/order-history-types";
import { TimeSpan, useUserBusinesses } from "api/custom";
import { useEffect, useMemo, useState } from "react";

export const useOnlineOrderHistory = () => {
  const userBusinessesQuery = useUserBusinesses();

  const [filters, setFilters] = useState<OnlineOrderFilters>({
    businessIds: [],
    businessOptions: [],
    page: 1,
    rowPerPage: 20,
  });

  useEffect(() => {
    if (userBusinessesQuery.data?.length) {
      const options: BusinessOption[] = userBusinessesQuery.data.map((b) => ({
        label: b.businessName,
        value: String(b.businessId),
      }));

      setFilters((prev) => ({
        ...prev,
        businessOptions: options,
        businessIds: prev.businessIds.length > 0 ? prev.businessIds : [],
      }));
    }
  }, [userBusinessesQuery.data]);

  const updateFilters = (updates: Partial<OnlineOrderFilters>) => {
    setFilters((prev) => {
      const newFilters = { ...prev, ...updates };

      if (
        updates.businessIds === undefined &&
        updates.dateRange === undefined &&
        updates.status === undefined &&
        updates.paymentType === undefined &&
        updates.provider === undefined
      ) {
        return newFilters;
      }

      if (updates.page === undefined) {
        newFilters.page = 1;
      }

      return newFilters;
    });
  };

  const resetFilters = () => {
    setFilters((prev) => ({
      businessIds: [],
      businessOptions: prev.businessOptions,
      page: 1,
      rowPerPage: 20,
      dateRange: undefined,
      status: undefined,
      paymentType: undefined,
      provider: undefined,
    }));
  };

  const queryParams: QueryParams = useMemo(() => {
    let startDate: string | undefined;
    let endDate: string | undefined;

    if (filters.dateRange?.from) {
      const fromDate = new Date(filters.dateRange.from);
      startDate = fromDate.toISOString();
    }

    if (filters.dateRange?.to) {
      const toDate = new Date(filters.dateRange.to);
      endDate = toDate.toISOString();
    }

    return {
      businessIds: filters.businessIds,
      start: startDate,
      end: endDate,
      page: filters.page,
      rowPerPage: filters.rowPerPage,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      orderStatus: filters.status ? [filters.status] : undefined,
      paymentType: filters.paymentType ? [filters.paymentType] : undefined,
      provider:
        filters.provider && filters.provider.length > 0
          ? filters.provider
          : undefined,
    };
  }, [filters]);

  const hasActiveFilters =
    !!filters.status ||
    !!filters.paymentType ||
    !!filters.dateRange ||
    (!!filters.provider && filters.provider.length > 0);

  return {
    filters,
    updateFilters,
    resetFilters,
    queryParams,
    hasActiveFilters,
  };
};

export const usePosOrderHistory = () => {
  const userBusinessesQuery = useUserBusinesses();

  const [filters, setFilters] = useState<PosOrderFilters>({
    businessIds: [],
    businessOptions: [],
    page: 1,
    rowPerPage: 20,
    dateRange: {
      from: new Date(),
      to: new Date(),
    },
  });

  useEffect(() => {
    if (userBusinessesQuery.data?.length) {
      const options: BusinessOption[] = userBusinessesQuery.data.map((b) => ({
        label: b.businessName,
        value: String(b.businessId),
      }));

      setFilters((prev) => ({
        ...prev,
        businessOptions: options,
        businessIds: prev.businessIds.length > 0 ? prev.businessIds : [],
      }));
    }
  }, [userBusinessesQuery.data]);

  const updateFilters = (updates: Partial<PosOrderFilters>) => {
    if (updates.businessIds && updates.businessIds.length > 1) {
      updates.businessIds = [
        updates.businessIds[updates.businessIds.length - 1],
      ];
    }

    setFilters((prev) => {
      const newFilters = { ...prev, ...updates };

      if (
        updates.businessIds === undefined &&
        updates.dateRange === undefined &&
        updates.status === undefined &&
        updates.paymentType === undefined
      ) {
        return newFilters;
      }

      if (updates.page === undefined) {
        newFilters.page = 1;
      }

      return newFilters;
    });
  };

  const resetFilters = () => {
    setFilters((prev) => ({
      businessIds: [],
      businessOptions: prev.businessOptions,
      page: 1,
      rowPerPage: 20,
      dateRange: {
        from: new Date(),
        to: new Date(),
      },
      status: undefined,
      paymentType: undefined,
    }));
  };

  const queryParams: QueryParams = useMemo(() => {
    const fromDate = filters.dateRange?.from
      ? new Date(filters.dateRange.from)
      : new Date();
    const toDate = filters.dateRange?.to
      ? new Date(filters.dateRange.to)
      : new Date();

    fromDate.setHours(0, 0, 0, 0);
    const startDate = fromDate.toISOString();

    toDate.setHours(23, 59, 59, 999);
    const endDate = toDate.toISOString();

    return {
      timeSpan: TimeSpan.custom,
      businessIds: filters.businessIds,
      start: startDate,
      end: endDate,
      page: filters.page,
      rowPerPage: filters.rowPerPage,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      orderStatus: filters.status ? [filters.status] : undefined,
      paymentTypePos: filters.paymentType ? [filters.paymentType] : undefined,
    };
  }, [filters]);
  const hasActiveFilters =
    !!filters.status || !!filters.paymentType || !!filters.dateRange;

  return {
    filters,
    updateFilters,
    resetFilters,
    queryParams,
    hasActiveFilters,
  };
};
